import * as React from 'react';
import { MoreVertical } from 'lucide-react';

import { cn } from '../lib/utils';
import { useMediaQuery } from '../hooks/use-media-query';
import { Button } from '../primitives/button';
import { Card } from '../primitives/card';
import { Checkbox } from '../primitives/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../primitives/dropdown-menu';
import { visibleActions, type ActionDefinition } from './action-definition';
import {
  filterColumnsByPriority,
  resolveBreakpoint,
  type ColumnPriority,
  type ResponsiveBreakpoint,
} from './responsive-columns';
import { VirtualTable, type VirtualTableColumn } from './virtual-table';

/**
 * Column descriptor for ResponsiveTableView — a VirtualTable column plus the
 * responsive metadata the archetype needs to collapse into cards.
 */
export interface ResponsiveTableColumn<Row> extends VirtualTableColumn<Row> {
  /**
   * Visibility priority: 1 (default) always shows, 2 needs >= md, 3 needs
   * >= lg. Drives `useResponsiveColumns` hiding on the desktop table.
   */
  priority?: ColumnPriority;
  /**
   * Where this column's cell lands on the card rendered below 768px:
   *   - `primary`   — the card title line (first primary column wins)
   *   - `secondary` — a labelled `header: value` row in the card body
   *   - `status`    — the badge slot in the card's top-right corner
   *   - `none`      — omitted from cards (e.g. a desktop-only timestamp)
   * Default: the first column is `primary`, the rest are `secondary`.
   */
  cardRole?: 'primary' | 'secondary' | 'status' | 'none';
}

/** Current breakpoint bucket from matchMedia (SSR-safe, assumes desktop first). */
export function useResponsiveBreakpoint(): ResponsiveBreakpoint {
  const isMd = useMediaQuery('(min-width: 768px)', { defaultValue: true });
  const isLg = useMediaQuery('(min-width: 1024px)', { defaultValue: true });
  return resolveBreakpoint(isMd, isLg);
}

/**
 * Filter a column list down to the ones visible at the current viewport,
 * per each column's `priority`. Reusable outside ResponsiveTableView for
 * DataTable-based views that want the same hiding behaviour.
 */
export function useResponsiveColumns<C extends { id: string; priority?: ColumnPriority }>(
  columns: readonly C[],
): C[] {
  const breakpoint = useResponsiveBreakpoint();
  return React.useMemo(
    () => filterColumnsByPriority(columns, breakpoint),
    [columns, breakpoint],
  );
}

export interface ResponsiveTableViewProps<Row> {
  columns: ResponsiveTableColumn<Row>[];
  data: Row[];
  /** Stable key per row — required so selection survives sorting/filtering. */
  rowKey: (row: Row, index: number) => string | number;
  /** Row activation (open detail). Fires on both table rows and cards. */
  onRowClick?: (row: Row, index: number) => void;
  /** Controlled selection (keys per `rowKey`). Enables checkboxes on cards. */
  selectedRows?: ReadonlyArray<string | number>;
  onSelectionChange?: (selected: Array<string | number>) => void;
  /** Per-row action menu (kebab on cards; available to custom cards too). */
  rowActions?: (row: Row, index: number) => ActionDefinition[];
  /** Full override for the card rendering below 768px. */
  renderCard?: (row: Row, index: number) => React.ReactNode;
  /** Table row height (desktop). */
  rowHeight?: number;
  /** Scroll-container height for the desktop table. */
  maxHeight?: number;
  ariaLabel?: string;
  className?: string;
}

/**
 * ResponsiveTableView — the table↔card-list archetype.
 *
 * At >= 768px it renders the virtualised table (priority-filtered columns);
 * below, each row becomes a Card with a primary line, labelled secondary
 * fields, a status-badge slot, an optional selection checkbox, and a kebab
 * action menu. One dataset + column list, two presentations — this is the
 * card-collapse pattern the dense-table modules (materials, inventory,
 * procurement, admin) are missing.
 */
export function ResponsiveTableView<Row>({
  columns,
  data,
  rowKey,
  onRowClick,
  selectedRows,
  onSelectionChange,
  rowActions,
  renderCard,
  rowHeight,
  maxHeight,
  ariaLabel,
  className,
}: ResponsiveTableViewProps<Row>) {
  const breakpoint = useResponsiveBreakpoint();
  const visibleColumns = React.useMemo(
    () => filterColumnsByPriority(columns, breakpoint),
    [columns, breakpoint],
  );

  if (breakpoint !== 'sm') {
    return (
      <VirtualTable<Row>
        rows={data}
        columns={visibleColumns}
        rowKey={rowKey}
        onRowClick={onRowClick}
        selectedRows={selectedRows}
        onSelectionChange={onSelectionChange}
        rowHeight={rowHeight}
        maxHeight={maxHeight}
        ariaLabel={ariaLabel}
        className={className}
      />
    );
  }

  return (
    <ul
      className={cn('flex flex-col gap-2', className)}
      aria-label={ariaLabel}
      data-testid="responsive-table-cards"
    >
      {data.map((row, index) => {
        const key = rowKey(row, index);
        return (
          <li key={key}>
            {renderCard ? (
              renderCard(row, index)
            ) : (
              <ResponsiveRowCard
                row={row}
                index={index}
                rowKeyValue={key}
                columns={columns}
                onRowClick={onRowClick}
                selectedRows={selectedRows}
                onSelectionChange={onSelectionChange}
                rowActions={rowActions}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

ResponsiveTableView.displayName = 'ResponsiveTableView';

interface ResponsiveRowCardProps<Row> {
  row: Row;
  index: number;
  rowKeyValue: string | number;
  columns: ResponsiveTableColumn<Row>[];
  onRowClick?: (row: Row, index: number) => void;
  selectedRows?: ReadonlyArray<string | number>;
  onSelectionChange?: (selected: Array<string | number>) => void;
  rowActions?: (row: Row, index: number) => ActionDefinition[];
}

function ResponsiveRowCard<Row>({
  row,
  index,
  rowKeyValue,
  columns,
  onRowClick,
  selectedRows,
  onSelectionChange,
  rowActions,
}: ResponsiveRowCardProps<Row>) {
  const primary =
    columns.find((c) => c.cardRole === 'primary') ??
    columns.find((c) => c.cardRole == null);
  const status = columns.find((c) => c.cardRole === 'status');
  const secondary = columns.filter(
    (c) => c !== primary && c !== status && c.cardRole !== 'none',
  );
  const actions = rowActions ? visibleActions(rowActions(row, index)) : [];
  const selectionEnabled = Boolean(onSelectionChange);
  const isSelected = Boolean(selectedRows?.includes(rowKeyValue));

  const toggleSelection = () => {
    if (!onSelectionChange) return;
    const current = new Set<string | number>(selectedRows ?? []);
    if (current.has(rowKeyValue)) {
      current.delete(rowKeyValue);
    } else {
      current.add(rowKeyValue);
    }
    onSelectionChange(Array.from(current));
  };

  return (
    <Card
      variant="outlined"
      className={cn('p-3', isSelected && 'border-primary/50 bg-primary/5')}
      data-testid="responsive-table-card"
      data-selected={selectionEnabled ? isSelected : undefined}
    >
      <div className="flex items-start gap-2">
        {selectionEnabled && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={toggleSelection}
            aria-label={`Select row ${rowKeyValue}`}
            className="mt-0.5"
          />
        )}

        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={onRowClick ? () => onRowClick(row, index) : undefined}
          disabled={!onRowClick}
        >
          <div className="flex items-start justify-between gap-2">
            <span className="truncate text-sm font-medium text-foreground">
              {primary ? primary.cell(row, index) : null}
            </span>
            {status && <span className="shrink-0">{status.cell(row, index)}</span>}
          </div>
          {secondary.length > 0 && (
            <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1">
              {secondary.map((c) => (
                <div key={c.id} className="min-w-0">
                  <dt className="truncate text-[11px] uppercase tracking-wide text-muted-foreground">
                    {c.header}
                  </dt>
                  <dd className="truncate text-xs text-foreground/90">
                    {c.cell(row, index)}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </button>

        {actions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                aria-label="Row actions"
              >
                <MoreVertical className="h-4 w-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {actions.map((a) => (
                <DropdownMenuItem
                  key={a.id}
                  disabled={a.disabled}
                  onSelect={a.onSelect}
                  className={cn(
                    a.intent === 'destructive' && 'text-destructive focus:text-destructive',
                  )}
                >
                  {a.icon && <span className="mr-2 inline-flex">{a.icon}</span>}
                  {a.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </Card>
  );
}
